
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../../../components/navbar";
import Footer from "../../../../../../../components/footer";
import Button from "../../../../../../../components/Button";
import FormInput from "../../../../../../../components/FormInput";
import styles from "../../../../css/Transactions/TransactionOptions/Statements/DebtorsAccountStatement.module.css";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getPatientCreditTransactions } from "../../../../../../../api/frontShop/DebtorsAPI";

function formatCurrency(amount) {
  const n = Number(amount || 0);
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function titleCase(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateLabel(month) {
  try {
    const d = new Date(`${month}-01T00:00:00`);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return "";
  }
}

function formatStatementDateLabel(month) {
  try {
    const [y, m] = String(month || "").split("-").map((v) => Number(v));
    if (!y || !m) return "";
    const end = new Date(y, m, 0);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(end);
  } catch {
    return "";
  }
}

export default function DebtorsAccountStatement() {
  const location = useLocation();
  const navigate = useNavigate();
  const transaction = location.state?.transaction || null;
  const patientId = transaction?.patientid != null ? Number(transaction.patientid) : null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [transactions, setTransactions] = useState([]);

  const [month, setMonth] = useState(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    return `${yyyy}-${mm}`;
  });

  const model = useMemo(() => {
    const filtered = month
      ? transactions.filter((t) => {
          const d = String(t?.transactiondate || "");
          return d.startsWith(`${month}-`);
        })
      : [];

    const ordered = [...filtered].sort((a, b) => {
      const da = new Date(a?.transactiondate || 0).getTime();
      const db = new Date(b?.transactiondate || 0).getTime();
      if (da !== db) return da - db;
      const aId = Number(a?.transactionid ?? a?.transactionId ?? a?.id ?? 0);
      const bId = Number(b?.transactionid ?? b?.transactionId ?? b?.id ?? 0);
      return aId - bId;
    });

    const openingBalance = ordered.length ? Number(ordered[0]?.balance || 0) : 0;

    const mappedRows = ordered.map((t) => {
      const txType = String(t?.type || "").toLowerCase();
      const amount = Number(t?.transactionamount || 0);
      const isInvoice = txType === "debit";
      const po = t?.purchaseorders || null;
      const recieptno = po?.recieptno || "";
      const paymentType = t?.paymenttype ? String(t.paymenttype) : "";

      return {
        txId: Number(t?.transactionid ?? t?.transactionId ?? t?.id ?? 0),
        date: t?.transactiondate ? String(t.transactiondate).slice(0, 10) : "",
        type: isInvoice ? "Invoice" : "Payment",
        details: isInvoice ? (t?.details || "") : `${paymentType || ""}${paymentType && recieptno ? " - " : ""}${recieptno || ""}`,
        debit: isInvoice ? amount : null,
        credit: isInvoice ? null : amount,
        balance: Number(t?.balance || 0),
      };
    });

    const rows = [
      {
        txId: 0,
        date: formatDateLabel(month),
        type: "Opening Balance",
        details: "Opening Balance",
        debit: null,
        credit: null,
        balance: openingBalance,
        isOpening: true,
      },
      ...mappedRows,
    ];

    const invoicedAmount = mappedRows.reduce((sum, r) => sum + (Number(r.debit) || 0), 0);
    const amountPaid = mappedRows.reduce((sum, r) => sum + (Number(r.credit) || 0), 0);
    const totalDue = mappedRows.length ? Number(mappedRows[mappedRows.length - 1]?.balance || 0) : openingBalance;

    return {
      statementTitle: "Statement of Accounts",
      pharmacyName: "Durban North Pharmacy",
      statementDateLabel: formatStatementDateLabel(month),
      customerName: transaction?.name || "Carlos Gonzalez",
      accountType: titleCase(transaction?.method || "Credit") || "Credit",
      rows,
      openingBalance,
      invoicedAmount,
      amountPaid,
      totalDue,
    };
  }, [month, transaction, transactions]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!patientId || !Number.isFinite(patientId)) {
        setTransactions([]);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await getPatientCreditTransactions(patientId);
        const json = res?.data;
        if (cancelled) return;
        setTransactions(Array.isArray(json?.data) ? json.data : []);
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [patientId, location.state?.refreshToken]);

  function downloadPdf() {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(14);
    doc.text("MediIntel", pageWidth / 2, 48, { align: "center" });

    doc.setFontSize(12);
    doc.text(model.statementTitle, 40, 86);
    doc.setFontSize(10);
    doc.text(`Statement Date: ${model.statementDateLabel}`, pageWidth - 40, 86, { align: "right" });
    doc.text(model.pharmacyName, 40, 104);

    doc.setFontSize(10);
    doc.text("To:", 40, 132);
    doc.text(model.customerName || "-", 40, 148);
    doc.text(`Account Type: ${model.accountType}`, 40, 164);

    autoTable(doc, {
      startY: 200,
      head: [["Date", "Type", "Details", "Debit", "Credit", "Balance"]],
      body: model.rows.map((r) => [
        r.date,
        r.type,
        r.details,
        r.debit == null ? "-" : formatCurrency(r.debit),
        r.credit == null ? "-" : formatCurrency(r.credit),
        formatCurrency(r.balance),
      ]),
      styles: { fontSize: 9, cellPadding: 6 },
      headStyles: { fillColor: [228, 214, 246], textColor: [17, 24, 39], fontStyle: "bold" },
      theme: "grid",
      didParseCell: (data) => {
        const row = model.rows[data.row.index];
        if (row?.isOpening && data.section === "body") {
          data.cell.styles.fillColor = [240, 249, 255];
        }
      },
    });

    const endY = doc.lastAutoTable?.finalY || 200;
    const boxX = pageWidth - 220;
    const boxY = endY + 55;
    doc.setDrawColor(228, 214, 246);
    doc.roundedRect(boxX, boxY, 180, 96, 8, 8);

    doc.setFontSize(10);
    doc.text("SUMMARY", boxX + 12, boxY + 18);
    doc.setFontSize(9);
    doc.text("Opening Balance", boxX + 12, boxY + 40);
    doc.text(formatCurrency(model.openingBalance), boxX + 168, boxY + 40, { align: "right" });
    doc.text("Invoiced Amount", boxX + 12, boxY + 56);
    doc.text(formatCurrency(model.invoicedAmount), boxX + 168, boxY + 56, { align: "right" });
    doc.text("Amount Paid", boxX + 12, boxY + 72);
    doc.text(formatCurrency(model.amountPaid), boxX + 168, boxY + 72, { align: "right" });

    doc.setFontSize(10);
    doc.text("Total Due", boxX + 12, boxY + 92);
    doc.text(formatCurrency(model.totalDue), boxX + 168, boxY + 92, { align: "right" });

    doc.save(`statement-${transaction?.id || "debtors"}.pdf`);
  }

  function proceedToPayment() {
    try {
      sessionStorage.setItem("debtors_total", String(model.totalDue || 0));
      if (patientId) sessionStorage.setItem("debtors_patientid", String(patientId));
    } catch {}
    navigate("/frontshop/debtors/payment-types", {
      state: {
        total: model.totalDue,
        fromDebtors: true,
        transaction,
        month,
        returnTo: location.pathname,
      },
    });
  }

  return (
    <div className={styles.wrapper}>
      <Navbar backTo="/frontshop/debtors/transactions/options" />

      <main className={styles.main}>
        <div className={styles.topRow}>
          <div className={styles.monthSelect} aria-label="Select month">
            <span style={{ marginRight: 16, fontWeight: 600, color: "#6b7280", fontSize: 14 }}></span>
            <FormInput
              label=""
              type="month"
              name="accountStatementMonth"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className={styles.monthInput}
            />
          </div>
        </div>

        <section className={styles.card}>
          <div className={styles.band}>MediIntel</div>

          <div className={styles.headerRow}>
            <div>
              <div className={styles.title}>{model.statementTitle}</div>
              <div className={styles.subTitle}>{model.pharmacyName}</div>
            </div>
            <div className={styles.headerRight}>Statement Date: {model.statementDateLabel}</div>
          </div>

          <div className={styles.topBoxes}>
            <div className={styles.toBox}>
              <div className={styles.toLabel}>To:</div>
              <div className={styles.toName}> {model.customerName}</div>
              <div className={styles.toAccount}>Account Type: {model.accountType}</div>
            </div>

            <aside className={styles.totalsCard}>
              <div className={styles.totalsRow}>
                <div className={styles.totalsLabel}>Opening Balance</div>
                <div className={styles.totalsValue}>{formatCurrency(model.openingBalance)}</div>
              </div>
              <div className={styles.totalsRow}>
                <div className={styles.totalsLabel}>Invoiced Amount</div>
                <div className={styles.totalsValue}>{formatCurrency(model.invoicedAmount)}</div>
              </div>
              <div className={styles.totalsRow}>
                <div className={styles.totalsLabel}>Amount Paid</div>
                <div className={styles.totalsValue}>{formatCurrency(model.amountPaid)}</div>
              </div>
              <div className={styles.totalsDivider} />
              <div className={styles.totalsRowTotal}>
                <div className={styles.totalsLabelTotal}>Total Due</div>
                <div className={styles.totalsValueTotal}>{formatCurrency(model.totalDue)}</div>
              </div>
            </aside>
          </div>

          <div className={styles.tableWrap}>
            <div className={styles.tableHeader}>
              <div>Date</div>
              <div>Type</div>
              <div>Details</div>
              <div className={styles.cellRight}>Debit</div>
              <div className={styles.cellRight}>Credit</div>
              <div className={styles.cellRight}>Balance</div>
            </div>
            <div className={styles.tableBody}>
              {error ? <div className={styles.error}>{error}</div> : null}
              {loading ? <div className={styles.loading}>Loading...</div> : null}
              {model.rows.map((r, idx) => (
                <div key={`${r.date}-${r.txId || idx}`} className={`${styles.tableRow} ${r.isOpening ? styles.rowOpening : ""}`}
                >
                  <div>{r.date}</div>
                  <div>
                    {r.isOpening ? (
                      <span className={styles.pillOpening}>{r.type}</span>
                    ) : (
                      <span className={`${styles.pill} ${r.type === "Payment" ? styles.pillPayment : styles.pillInvoice}`}>{r.type}</span>
                    )}
                  </div>
                  <div>{r.details}</div>
                  <div className={styles.cellRight}>{r.debit == null ? "-" : formatCurrency(r.debit)}</div>
                  <div className={styles.cellRight}>{r.credit == null ? "-" : formatCurrency(r.credit)}</div>
                  <div className={styles.cellRight}>{formatCurrency(r.balance)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.bottomRow}>
            <div className={styles.totalDue}>
              <div className={styles.totalDueLabel}>TOTAL DUE</div>
              <div className={styles.totalDueValue}>{formatCurrency(model.totalDue)}</div>
            </div>

            <div className={styles.actions}>
              <Button variant="primary" className={styles.downloadBtn} onClick={downloadPdf}>
                Download
              </Button>
              <Button variant="primary" className={styles.proceedBtn} onClick={proceedToPayment}>
                Proceed to payment
              </Button>
            </div>
          </div>
        </section>
      </main>

      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
